import React, { useMemo } from "react";
import styled, { css } from "styled-components";
import { Environment } from "@core/config/environment";
import { ImageVersions } from "@domain/media/dtos";
import { useSourceLoaded } from "@domain/media/hooks";
import { Media } from "@domain/media/media";

interface BackgroundImageProps extends React.HTMLAttributes<HTMLDivElement> {
	image?: Media;
	backdrop?: boolean;
	defaultUrl?: string;
	size?: ImageVersions;
	forFeed?: boolean;
	forSearch?: boolean;
	insideRadius?: boolean;
	forPage?: boolean;
	isHovered?: boolean;
	rgbaColor?: string;
	isBackLinearGradient?: boolean;
	fullGradient?: boolean;
}
export const BackgroundImage: React.FC<BackgroundImageProps> = ({
	image,
	backdrop,
	defaultUrl,
	size,
	children,
	forFeed = false,
	forSearch = false,
	insideRadius = false,
	forPage = false,
	isHovered = false,
	isBackLinearGradient = false,
	fullGradient = false,
	rgbaColor = Environment.APP_VARIANT === "jaangal" ? "0, 134, 58, 0.55" : "32, 106, 248, 0.55",
	...htmlProps
}) => {
	const finalUrl = useMemo(
		() =>
			defaultUrl
				? defaultUrl
				: image
					? size !== undefined && image.versions?.[size] !== undefined
						? image.versions[size]
						: image.url
					: undefined,
		[image, defaultUrl, size]
	);

	const sourceLoaded = useSourceLoaded(finalUrl);

	return (
		<Container forPage={forPage} insideRadius={insideRadius} forSearch={forSearch} forFeed={forFeed} {...htmlProps}>
			{!sourceLoaded && image?.metadata?.placeholder ? (
				<Background
					insideRadius={insideRadius}
					forSearch={forSearch}
					forFeed={forFeed}
					forPage={forPage}
					url={image.metadata.placeholder}
					isHovered={isHovered}
					rgbaColor={rgbaColor}
				/>
			) : null}
			{sourceLoaded && (
				<Background
					insideRadius={insideRadius}
					forSearch={forSearch}
					forFeed={forFeed}
					id={image?.id}
					forPage={forPage}
					url={sourceLoaded}
					isHovered={isHovered}
					rgbaColor={rgbaColor}
				/>
			)}
			{backdrop && (
				<Backdrop
					forPage={forPage}
					insideRadius={insideRadius}
					forSearch={forSearch}
					forFeed={forFeed}
					isHovered={isHovered}
					rgbaColor={rgbaColor}
					isBackLinearGradient={isBackLinearGradient}
					fullGradient={fullGradient}
				/>
			)}
			<Content className="content">{children}</Content>
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => !["insideRadius", "forFeed", "forPage", "forSearch"].includes(prop),
})<{ insideRadius?: boolean; forFeed?: boolean; forPage?: boolean; forSearch?: boolean }>`
	position: relative;
`;

const Content = styled.div`
	position: relative;
	height: 100%;
`;

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const hoverEffect = (isHovered?: boolean, rgbaColor?: string) => css`
	-webkit-transition: background 0.5s ease-out;
	-moz-transition: background 0.5s ease-out;
	-o-transition: background 0.5s ease-out;
	transition: background 0.5s ease-out;
	background-color: ${isHovered && `rgba(${rgbaColor})`};
`;

const Background = styled.div.withConfig({
	shouldForwardProp: (prop) =>
		!["url", "forPage", "forFeed", "insideRadius", "forSearch", "isHovered", "rgbaColor"].includes(prop),
})<{
	url?: string;
	forPage?: boolean;
	forFeed?: boolean;
	insideRadius?: boolean;
	forSearch?: boolean;
	isHovered?: boolean;
	rgbaColor: string;
}>`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-image: url(${({ url }) => url});
	background-repeat: no-repeat;
	background-position: 50% 50%;
	background-size: cover;
	${({ forFeed }) => (!forFeed ? "" : `height: 100%% !important`)};
	${({ insideRadius }) =>
		!insideRadius ? "" : `border: solid 1px white;border-bottom-left-radius: 12px;border-bottom-right-radius: 12px;`};
	${({ forPage }) => (!forPage ? "" : `border: solid 1px white;border-radius: 25px;`)};
	${({ isHovered, rgbaColor }) => hoverEffect(isHovered, rgbaColor)}
`;

const Backdrop = styled.div.withConfig({
	shouldForwardProp: (prop) =>
		![
			"forFeed",
			"forPage",
			"forSearch",
			"insideRadius",
			"isHovered",
			"rgbaColor",
			"isBackLinearGradient",
			"fullGradient",
		].includes(prop),
})<{
	forFeed?: boolean;
	forPage?: boolean;
	forSearch?: boolean;
	insideRadius?: boolean;
	isHovered?: boolean;
	rgbaColor: string;
	isBackLinearGradient?: boolean;
	fullGradient?: boolean;
}>`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	${({ forFeed }) => (!forFeed ? "" : `height: 100% !important`)};
	${({ insideRadius }) =>
		!insideRadius ? "" : `border: solid 1px white;border-bottom-left-radius: 12px;border-bottom-right-radius: 12px;`};
	${({ forPage }) => (!forPage ? "" : `border: solid 1px white;border-radius: 25px;`)};

	${({ isHovered, rgbaColor }) => hoverEffect(isHovered, rgbaColor)}
	${({ isBackLinearGradient, fullGradient }) =>
		isBackLinearGradient
			? "background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(crowdfunding.png);"
			: `background-image: linear-gradient(to right, rgba(0, 0, 0, ${fullGradient ? 0.5 : 0.75}) ${
					fullGradient ? "100%" : "25%"
				}, rgba(0, 0, 0, 0));`};
`;
